import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { ChangePasswordDialogComponent, PasswordDialogData } from './../../../components/dialogs/change-password-dialog/change-password-dialog.component';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { SimpleAlertDialogComponent, SimpleDialogData } from './../../../components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from './../../../services/auth-service';
import { PathResolveService } from './../../../services/path-resolve.service';
import { Router } from '@angular/router';
import { ChangeDetectionStrategy, Component, OnDestroy, Input, ViewChild } from '@angular/core';
import { FriendRequestApiService } from 'src/app/data-transfer/services/friend-request-api-service';
import { ColorThemeService, COLOR_THEME_DARK, COLOR_THEME_LIGHT } from '../../services/color-theme-service';
import { MatDialog } from '@angular/material/dialog';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { PROJECT_IMAGES_PATH } from 'src/app/model/paths';
import { LanguageHandler } from 'src/app/util/language-handler';
import { ContactInfoDialogComponent } from 'src/app/components/dialogs/contact-info-dialog/contact-info-dialog.component';
import { SearchRequestDto } from 'src/app/data-transfer/entities/search-dto/search-request-dto';
import { SearchResultsComponent } from '../search-results/search-results.component';
import { FormControl } from '@angular/forms';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { NotificationMenuComponent } from 'src/app/components/various-components/notifications/notification-menu/notification-menu.component';
import { FriendRequestDialogComponent } from 'src/app/components/dialogs/friend-request-dialog/friend-request-dialog.component';
import { ManageFriendRequestsDialogComponent } from 'src/app/components/dialogs/manage-friend-requests-dialog/manage-friend-requests-dialog.component';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';

@Component({
  selector: 'app-main-navigation',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MainNavComponent implements OnDestroy {

  @Input() isMinimalLayout = false;

  @ViewChild(NotificationMenuComponent) notificationMenuComponent!:NotificationMenuComponent;

  username = '';
  countryCodes: string[] = [];
  isDarkTheme = false;
  isAdmin: boolean;
  isValidator: boolean;
  isContractSigned: boolean;
  currentUserId: number;
  packagingPart = PackagingPart;
  imageSource = '';
  pendingInvitesCount$: Observable<number>;

  private _allDataExchangeNotifications = 0;

  private languageHandler: LanguageHandler;
  private dialogSubscription?: Subscription;
  private changePassSubscription?: Subscription;
  private signOutSubscription?: Subscription;
  private themeSubscription?: Subscription;
  private dataExchangeNotificationsSubscription?: Subscription;

  searchRequestDto?: SearchRequestDto;
  searchControl = new FormControl<string>('');

  colorThemes = [
    { id: 1, label: COLOR_THEME_LIGHT, name: this.translateService.translate('dataManagement.userData.texts.colorTheme.light') },
    { id: 2, label: COLOR_THEME_DARK, name: this.translateService.translate('dataManagement.userData.texts.colorTheme.dark') }];


  constructor(
    private authService: AuthService,
    private router: Router,
    private translateService: TranslocoService,
    private dialog: MatDialog,
    private userApiService: UserApiService,
    private colorThemeService: ColorThemeService,
    private importExportApiService: ImportExportApiService,
    private friendRequestApiService: FriendRequestApiService,
    private dataExchangeRequestApiService: DataExchangeRequestApiService
  ) {
    this.username = this.authService.getSignedInUsername();
    this.currentUserId = authService.getUserId();
    this.isAdmin = this.authService.isUserAdmin();
    this.isValidator = this.authService.isUserValidator();
    this.isContractSigned = this.authService.isContractSigned();

    this.themeSubscription = this.colorThemeService.colorThemeSubject.subscribe((nextValue) => {
      this.isDarkTheme = nextValue === COLOR_THEME_DARK;
      this.imageSource = `${PROJECT_IMAGES_PATH}logo/logo_paco${this.isDarkTheme ? '_light' : '_dark'}.svg`;
    });
    this.languageHandler = new LanguageHandler(this.translateService);
    this.countryCodes = this.languageHandler.getLanguageCodes();
    this.pendingInvitesCount$ = this.friendRequestApiService.pendingInvitesCount$;

    this.dataExchangeNotificationsSubscription = combineLatest([
      this.dataExchangeRequestApiService.unseenSentDataExchangeRequests$,
      this.dataExchangeRequestApiService.unseenReceivedDataExchangeRequests$
    ]).subscribe(([sent, received]) => {
      this._allDataExchangeNotifications = sent + received;
    });
  }

  clickHome() {
    this.router.navigate([PathResolveService.getStartPath(true)]);
  }

  clickPackagingSystemHome() {
    const path = PathResolveService.getPackagingSystemHomepagePath(true);
    this.router.navigate([path]);
  }

  clickPackagingUnitHome() {
    const path = PathResolveService.getPackagingUnitHomepagePath(true);
    this.router.navigate([path]);
  }

  clickComponentsHome() {
    this.router.navigate([PathResolveService.getComponentHomepagePath(true)]);
  }

  clickMaterialHome() {
    const path = PathResolveService.getMaterialHomepagePath(true);
    this.router.navigate([path]);
  }

  clickTags() {
    const path = PathResolveService.getTagsManagementPath(true);
    this.router.navigate([path]);
  }

  clickSignOut() {
    this.signOut();
  }

  private async signOut() {
    this.signOutSubscription = this.authService.signOut().subscribe(_ => this.router.navigateByUrl('/login'));
  }

  clickContactInfo() {
    const dialogConfig = getDialogConfig(null, '800px');
    const dialogRef = this.dialog.open(ContactInfoDialogComponent, dialogConfig);
  }

  clickChangePassword() {
    const dialogData = new PasswordDialogData(this.currentUserId, this.username, this.isAdmin);
    const dialogConfig = getDialogConfig(dialogData, '600px');
    const dialogRef = this.dialog.open(ChangePasswordDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) { return; }
      this.changePassSubscription = this.userApiService.changePassword(result.data, true).subscribe({
        next: _ => {},
        error: e => {
          const errorData: SimpleDialogData = { title: 'Error', messages: [e.error], icon: 'error' };
          const errorDialogConfig = getDialogConfig(errorData, '600px');
          this.dialog.open(SimpleAlertDialogComponent, errorDialogConfig);
        },
      });
    });
  }

  clickFriendRequests() {
    const dialogConfig = getDialogConfig(null, '600px');
    this.dialog.open(FriendRequestDialogComponent, dialogConfig);
  }

  clickManageFriendRequests() {
    const dialogConfig = getDialogConfig(null, '800px');
    const dialogRef = this.dialog.open(ManageFriendRequestsDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      this.friendRequestApiService.reloadPendingInvites(true);
    });
  }

  setColorTheme(theme: number) {
    this.colorThemeService.colorTheme = this.colorThemes.find(x => x.id === theme)?.label ?? COLOR_THEME_LIGHT;
  }

  setLanguage(countryCode: string) {
    this.languageHandler.setLanguage(countryCode);
  }

  getButtonClass(path: string) {
    const isActiveRoute =
      this.router.isActive(path, { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' });
    return isActiveRoute ? this.isDarkTheme ? 'active-dark-theme' : 'active-light-theme' : '';
  }

  getPackagingPartImage(packagingPart: number) {
    let imagePath = `${PROJECT_IMAGES_PATH}icons/`;
    switch (packagingPart) {
      case PackagingPart.System: imagePath = imagePath.concat('system'); break;
      case PackagingPart.Unit: imagePath = imagePath.concat('unit'); break;
      case PackagingPart.Component: imagePath = imagePath.concat('component'); break;
      case PackagingPart.Material: imagePath = imagePath.concat('material'); break;
      default: break;
    }
    if (this.isDarkTheme) { imagePath = imagePath.concat('_dark'); }
    return imagePath.concat('.svg');
  }

  onSearch(): void {
    const query = this.searchControl.value;
    if (!this.isValidQuery(query)) {
      this.openSearchWarningDialog()
      return;
    }


    if (query && query.trim().length > 0) {
      this.dialog.open(SearchResultsComponent, {
        width: '80%',
        height: "65vh",
        data: {
          query
         }
      });
    }
    this.clearSearchBar();
  }

  isValidQuery(query:any) {
    if (!query) return false;

    const allDigits = /^\d+$/.test(query);
    if (allDigits) return true;

    return query.length > 3;
  }

  clearSearchBar():void{
    this.searchControl.setValue('');
  }

  protected openSearchWarningDialog() {
    const dialogData: SimpleDialogData = {
      title: this.translateService.translate('common.text.warning'),
      messages: [this.translateService.translate('errors.searchWarning')],
      icon: 'warning'
    };

    this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(dialogData));
  }

  get allNotifications() : number {
    return this._allDataExchangeNotifications + this.importExportApiService.allNotifications;
  }
  updateNotificationMenu(){
    this.notificationMenuComponent.createNotifications();
  }

  ngOnDestroy(): void {
    this.dialogSubscription?.unsubscribe();
    this.changePassSubscription?.unsubscribe();
    this.signOutSubscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
    this.dataExchangeNotificationsSubscription?.unsubscribe();
  }
}
